import type { RobotSchedule } from '@/api/robot-schedule.api'
import type { RobotFormValues } from '@/components/form/RobotForm'
import { DAY_OPTIONS, DEFAULT_WORKING_DAYS } from './constants'
import type {
  GeneralWorkSchedule,
  Position,
  ScheduleChangePayload,
  TimeRange,
  WorkingDayChangePayload,
  WorkingDayEntry,
} from './types'

export const pickVoiceId = (voice: unknown): string | undefined => {
  if (!voice) return undefined
  if (typeof voice === 'object') {
    const voiceObj = voice as { collectionId?: string; id?: string }
    return voiceObj.collectionId || voiceObj.id
  }
  return String(voice)
}

export const normalizeHHmm = (val: string): string => {
  if (!val) return ''
  const t = val.includes('T') ? val.split('T')[1] : val
  return t.slice(0, 5)
}

export const toIsoTime = (hhmm: string): string => {
  const [h = '00', m = '00'] = hhmm.split(':')
  const now = new Date()
  const yyyy = now.getFullYear()
  const MM = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  const HH = h.padStart(2, '0')
  const mm = m.padStart(2, '0')
  return `${yyyy}-${MM}-${dd}T${HH}:${mm}:00.000Z`
}

export const buildRobotPayload = (
  values: RobotFormValues,
  extras?: Record<string, unknown>,
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    ...values,
    lowBatteryThreshold: values.lowBatteryThreshold || 10,
    ...extras,
  }

  delete payload.workTime
  delete payload.minEnergy

  Object.keys(payload).forEach(
    (k) => payload[k] === undefined && delete payload[k],
  )
  return payload
}

export const mapSchedulesToTimeRanges = (
  schedules: RobotSchedule[],
): TimeRange[] =>
  schedules.map((s) => ({
    scheduleId: s.id,
    from: s.startTime,
    to: s.endTime,
    name: s.name,
    workingDays: s.workingDays,
    status: s.status,
  }))

export const extractScheduleIds = (schedules: RobotSchedule[]): string[] =>
  schedules.map((s) => s.id).filter((id): id is string => !!id)

export const buildScheduleChanges = (
  positions: Position[],
): ScheduleChangePayload => {
  const newScheduleList: ScheduleChangePayload['newScheduleList'] = []
  const updateScheduleList: ScheduleChangePayload['updateScheduleList'] = []
  const deleteScheduleIdList: string[] = []

  for (const pos of positions) {
    if (!pos.scheduleLoaded) continue
    const survivingIds = new Set<string>()

    for (const tr of pos.timeRanges) {
      if (!tr.from || !tr.to) continue

      const base = {
        startTime: toIsoTime(tr.from),
        endTime: toIsoTime(tr.to),
        locationId: pos.id,
      }

      if (tr.scheduleId) {
        survivingIds.add(tr.scheduleId)
        updateScheduleList.push({
          ...base,
          id: tr.scheduleId,
          status: tr.status ?? 1,
        })
      } else {
        newScheduleList.push(base)
      }
    }

    for (const id of pos.initialScheduleIds ?? []) {
      if (!survivingIds.has(id)) deleteScheduleIdList.push(id)
    }
  }

  return { newScheduleList, updateScheduleList, deleteScheduleIdList }
}

export const buildWorkingDayChanges = (
  schedules: GeneralWorkSchedule[],
  oldDays: WorkingDayEntry[],
): WorkingDayChangePayload => {
  const availableOld = [...oldDays]
  const createdList: WorkingDayChangePayload['createdList'] = []
  const updatedList: WorkingDayChangePayload['updatedList'] = []

  const newFlat: Array<{
    dayOfWeek: number
    startTime: string
    endTime: string
  }> = []
  schedules.forEach((s) => {
    if (!s.workFrom || !s.workTo) return
    s.days.forEach((d) => {
      newFlat.push({
        dayOfWeek: Number(d),
        startTime: s.workFrom,
        endTime: s.workTo,
      })
    })
  })

  newFlat.forEach((newItem) => {
    const exactIdx = availableOld.findIndex(
      (o) =>
        o.dayOfWeek === newItem.dayOfWeek &&
        normalizeHHmm(o.startTime) === normalizeHHmm(newItem.startTime) &&
        normalizeHHmm(o.endTime) === normalizeHHmm(newItem.endTime),
    )

    if (exactIdx >= 0) {
      availableOld.splice(exactIdx, 1)
      return
    }

    const dayIdx = availableOld.findIndex(
      (o) => o.dayOfWeek === newItem.dayOfWeek,
    )
    if (dayIdx >= 0) {
      const old = availableOld[dayIdx]
      updatedList.push({
        id: old.id,
        dayOfWeek: newItem.dayOfWeek,
        startTime: newItem.startTime,
        endTime: newItem.endTime,
        status: old.status ?? 1,
      })
      availableOld.splice(dayIdx, 1)
      return
    }

    createdList.push(newItem)
  })

  const deletedIdList: string[] = []
  availableOld.forEach((old) => {
    deletedIdList.push(old.id)
    updatedList.push({
      id: old.id,
      dayOfWeek: old.dayOfWeek,
      startTime: old.startTime,
      endTime: old.endTime,
      status: 0,
    })
  })

  return { createdList, updatedList, deletedIdList }
}

export const buildGeneralSchedulesFromRobot = (robot: {
  robotWorkingDays?: WorkingDayEntry[]
  workTime?: unknown
  startTime?: string
  endTime?: string
}): GeneralWorkSchedule[] => {
  const existingSchedules: GeneralWorkSchedule[] = []

  if (robot.robotWorkingDays !== undefined) {
    if (robot.robotWorkingDays.length > 0) {
      const groupMap = new Map<string, GeneralWorkSchedule>()
      robot.robotWorkingDays.forEach((wd) => {
        const st = normalizeHHmm(wd.startTime)
        const et = normalizeHHmm(wd.endTime)
        const key = `${st}-${et}`

        if (!groupMap.has(key)) {
          groupMap.set(key, {
            id: wd.id || crypto.randomUUID(),
            days: [],
            workFrom: st,
            workTo: et,
          })
        }
        groupMap.get(key)!.days.push(String(wd.dayOfWeek))
      })
      existingSchedules.push(...Array.from(groupMap.values()))
    }
    return existingSchedules
  }

  const wt = robot.workTime
  const start =
    typeof wt === 'object' && wt
      ? (wt as { start?: string }).start
      : (robot.startTime ?? '')
  const end =
    typeof wt === 'object' && wt
      ? (wt as { end?: string }).end
      : (robot.endTime ?? '')

  if (start || end) {
    existingSchedules.push({
      id: Date.now().toString(),
      days: ['2', '3', '4', '5', '6'],
      workFrom: start ? start.slice(0, 5) : '',
      workTo: end ? end.slice(0, 5) : '',
    })
  }

  return existingSchedules
}

const toMinutes = (hhmm: string): number | null => {
  if (!hhmm) return null
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim())
  if (!m) return null
  const h = Number(m[1])
  const mm = Number(m[2])
  if (h < 0 || h > 23 || mm < 0 || mm > 59) return null
  return h * 60 + mm
}

const toIntervals = (
  fromMin: number,
  toMin: number,
): Array<[number, number]> => {
  if (fromMin === toMin) return []
  if (fromMin < toMin) return [[fromMin, toMin]]
  return [
    [fromMin, 1440],
    [0, toMin],
  ]
}

const intervalsOverlap = (
  a: Array<[number, number]>,
  b: Array<[number, number]>,
): boolean => {
  for (const [as, ae] of a) {
    for (const [bs, be] of b) {
      if (as < be && bs < ae) return true
    }
  }
  return false
}

const parseWorkingDays = (wd?: string): Set<string> =>
  new Set(
    (wd || DEFAULT_WORKING_DAYS)
      .split(',')
      .map((d) => d.trim())
      .filter(Boolean),
  )

const daysIntersect = (a: Set<string>, b: Set<string>): boolean => {
  for (const d of a) if (b.has(d)) return true
  return false
}

const withinMainHours = (
  fromMin: number,
  toMin: number,
  mainFrom: number,
  mainTo: number,
): boolean => {
  const slot = toIntervals(fromMin, toMin)
  const main = toIntervals(mainFrom, mainTo)
  return slot.every(([ss, se]) => main.some(([ms, me]) => ss >= ms && se <= me))
}

export const validatePositions = (
  schedules: GeneralWorkSchedule[],
  positions: Position[],
): string[] => {
  const errors: string[] = []

  const validMainSchedules = schedules
    .map((s, idx) => {
      const from = toMinutes(s.workFrom)
      const to = toMinutes(s.workTo)

      if (s.workFrom || s.workTo) {
        if (from == null || to == null) {
          errors.push(
            `Thiết lập chung (khung ${idx + 1}): thiếu hoặc sai định dạng giờ.`,
          )
        } else if (from === to) {
          errors.push(
            `Thiết lập chung (khung ${idx + 1}): giờ bắt đầu và kết thúc trùng nhau.`,
          )
        }
        if (s.days.length === 0) {
          errors.push(
            `Thiết lập chung (khung ${idx + 1}): vui lòng chọn ngày làm việc.`,
          )
        }
      }

      return { from, to, days: new Set(s.days), label: `khung ${idx + 1}` }
    })
    .filter(
      (s) =>
        s.from != null && s.to != null && s.from !== s.to && s.days.size > 0,
    )

  for (let i = 0; i < validMainSchedules.length; i++) {
    for (let j = i + 1; j < validMainSchedules.length; j++) {
      const a = validMainSchedules[i]
      const b = validMainSchedules[j]

      if (daysIntersect(a.days, b.days)) {
        if (
          intervalsOverlap(
            toIntervals(a.from!, a.to!),
            toIntervals(b.from!, b.to!),
          )
        ) {
          const sharedDays = Array.from(a.days)
            .filter((d) => b.days.has(d))
            .map(
              (d) =>
                DAY_OPTIONS.find((opt) => opt.value === Number(d))?.label ||
                `Thứ ${d}`,
            )
            .join(', ')

          errors.push(
            `Trùng lặp thời gian trong Thiết lập chung: ${a.label} bị trùng với ${b.label} vào các ngày (${sharedDays}).`,
          )
        }
      }
    }
  }

  const hasMainHours = validMainSchedules.length > 0

  type Slot = {
    posName: string
    posId: string
    from: number
    to: number
    days: Set<string>
    label: string
  }
  const slots: Slot[] = []

  for (const pos of positions) {
    if (!pos.scheduleLoaded) continue
    pos.timeRanges.forEach((tr, idx) => {
      if (!tr.from && !tr.to) return

      const f = toMinutes(tr.from)
      const t = toMinutes(tr.to)
      const label = `${pos.name} (khung ${idx + 1}: ${tr.from || '?'}–${tr.to || '?'})`
      const slotDays = parseWorkingDays(tr.workingDays)

      if (f == null || t == null) {
        errors.push(`${label}: thiếu hoặc sai định dạng giờ.`)
        return
      }
      if (f === t) {
        errors.push(`${label}: giờ bắt đầu và kết thúc trùng nhau.`)
        return
      }

      if (hasMainHours) {
        const isCovered = validMainSchedules.some(
          (ms) =>
            daysIntersect(ms.days, slotDays) &&
            withinMainHours(f, t, ms.from!, ms.to!),
        )

        if (!isCovered) {
          errors.push(
            `${label}: nằm ngoài các khung giờ làm việc chính hoặc ngày làm việc không khớp.`,
          )
          return
        }
      }

      slots.push({
        posName: pos.name,
        posId: pos.id,
        from: f,
        to: t,
        days: slotDays,
        label,
      })
    })
  }

  const reported = new Set<string>()
  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      const a = slots[i]
      const b = slots[j]

      if (!daysIntersect(a.days, b.days)) continue

      if (
        intervalsOverlap(toIntervals(a.from, a.to), toIntervals(b.from, b.to))
      ) {
        const key = [a.label, b.label]
          .sort((x, y) => x.localeCompare(y))
          .join(' | ')
        if (reported.has(key)) continue
        reported.add(key)

        if (a.posId === b.posId) {
          errors.push(
            `Trùng thời gian trong cùng vị trí "${a.posName}": ${a.label} bị trùng lặp với ${b.label}.`,
          )
        } else {
          errors.push(
            `Trùng thời điểm giữa "${a.posName}" và "${b.posName}": robot không thể ở hai vị trí cùng lúc (${a.label} ⟷ ${b.label}).`,
          )
        }
      }
    }
  }

  return errors
}
